// UI Configuration
// This file loads and exports UI configuration from uiConfig.json

import uiConfigJson from './uiConfig.json';

// Currency Configuration
export interface CurrencyConfig {
  symbol: string;
  names: {
    major: string;
    minor: string;
  };
  locale: string;
  useIndianNumbering: boolean; // For INR, uses Lakhs/Crores instead of Thousands/Millions
}

export const CURRENCY_CONFIG: Record<string, CurrencyConfig> = {
  'INR': {
    symbol: '₹',
    names: { major: 'Rupees', minor: 'Paise' },
    locale: 'en-IN',
    useIndianNumbering: true,
  },
  'USD': {
    symbol: '$',
    names: { major: 'Dollars', minor: 'Cents' },
    locale: 'en-US',
    useIndianNumbering: false,
  },
  'EUR': {
    symbol: '€',
    names: { major: 'Euros', minor: 'Cents' },
    locale: 'de-DE',
    useIndianNumbering: false,
  },
  'GBP': {
    symbol: '£',
    names: { major: 'Pounds', minor: 'Pence' },
    locale: 'en-GB',
    useIndianNumbering: false,
  },
  'JPY': {
    symbol: '¥',
    names: { major: 'Yen', minor: 'Sen' },
    locale: 'ja-JP',
    useIndianNumbering: false,
  },
  'CNY': {
    symbol: '¥',
    names: { major: 'Yuan', minor: 'Fen' },
    locale: 'zh-CN',
    useIndianNumbering: false,
  },
  'AUD': {
    symbol: 'A$',
    names: { major: 'Australian Dollars', minor: 'Cents' },
    locale: 'en-AU',
    useIndianNumbering: false,
  },
  'CAD': {
    symbol: 'C$',
    names: { major: 'Canadian Dollars', minor: 'Cents' },
    locale: 'en-CA',
    useIndianNumbering: false,
  },
};

// Helper function to get currency config
export const getCurrencyConfig = (currencyCode: string): CurrencyConfig => {
  const code = currencyCode.toUpperCase();
  return CURRENCY_CONFIG[code] || {
    symbol: code,
    names: { major: code, minor: 'Cents' },
    locale: 'en-IN',
    useIndianNumbering: false,
  };
};

export interface UIConfig {
  reports: {
    rmInventoryReport: {
      defaultCategory: string;
    };
    materialIssueReport: {
      defaultCategory: string;
      allowedCategories: string[];
    };
    scrapReport: {
      materialIssueCategory: string;
    };
    /** HSN report: map API UOM codes (keys matched case-insensitively) to display labels */
    hsnReport: {
      uomLabels: Record<string, string>;
    };
  };
  pagination: {
    defaultItemsPerPage: number;
    itemsPerPageOptions: number[];
  };
  workOrder: {
    minRemainingQuantity: number;
  };
  dashboard: {
    rmDashboard: {
      agedInventoryMinQuantity: number;
      agedInventoryItemsPerPage: number;
    };
  };
  materialPlanning: {
    cutoffDate: number;
  };
  masterPages: {
    showColorVariant: boolean;
  };
  branding: {
    /**
     * File name under public/customer-logo (e.g. "sg-group-logo.jpg").
     * Empty string hides the client logo in the app sidebar header.
     */
    clientLogoFileName: string;
  };
  /** RM QR label payload: IB24|{orgCode}|RM|v1|SKU=...|HEAT=... */
  rmQr?: {
    orgCode: string;
  };
  invoice: {
    listPageSize: number;
    einvoiceEnabledBranchCodes: string[];
    /** Role names (case-insensitive) allowed to cancel an e-invoice IRN */
    einvoiceCancelAllowedRoleNames: string[];
    regularSupplies?: { code: string; name: string; description?: string; note?: string }[];
    modesOfTransportation: string[];
  };
}

// Export the config with type safety
export const uiConfig: UIConfig = uiConfigJson as UIConfig;

// Helper functions to access specific config values
export const getRMInventoryReportConfig = () => uiConfig.reports.rmInventoryReport;
export const getMaterialIssueReportConfig = () => uiConfig.reports.materialIssueReport;
export const getScrapReportConfig = () => uiConfig.reports.scrapReport;
export const getHsnReportConfig = () => uiConfig.reports.hsnReport;
export const getPaginationConfig = () => uiConfig.pagination;
export const getWorkOrderConfig = () => uiConfig.workOrder;
export const getRMDashboardConfig = () => ({
  agedInventoryMinQuantity: uiConfig.dashboard.rmDashboard.agedInventoryMinQuantity,
  agedInventoryItemsPerPage: uiConfig.dashboard.rmDashboard.agedInventoryItemsPerPage,
});
export const getMaterialPlanningConfig = () => uiConfig.materialPlanning;
export const getMasterPagesConfig = () => uiConfig.masterPages;
export const getBrandingConfig = () => uiConfig.branding;
export const getRmQrConfig = () => uiConfig.rmQr ?? { orgCode: 'SG-GROUP' };
export const getInvoiceConfig = () => uiConfig.invoice;

/** Absolute path to client logo under /customer-logo, or null if not configured. */
export function getClientLogoUrl(): string | null {
  const fileName = String(uiConfig.branding?.clientLogoFileName ?? '').trim();
  if (!fileName) return null;
  return `/customer-logo/${fileName.replace(/^\/+/, '')}`;
}

/** e-invoice supply types with no GST (without payment of tax). */
export const REGULAR_SUPPLY_CODES_WITHOUT_TAX = new Set(['SEZWOP', 'EXPWOP']);

export function isSupplyTypeWithoutTax(code: string | undefined | null): boolean {
  return REGULAR_SUPPLY_CODES_WITHOUT_TAX.has(String(code ?? '').trim().toUpperCase());
}

/** Statutory / supply note from uiConfig regularSupplies (e.g. SEZWP line 52). */
export function getRegularSupplyNote(code: string | undefined | null): string {
  const supplies = getInvoiceConfig().regularSupplies ?? [];
  const key = String(code ?? '').trim().toUpperCase();
  if (!key) return '';
  const match = supplies.find((s) => String(s.code).trim().toUpperCase() === key);
  return String(match?.note ?? '').trim();
}

export function getInvoiceSupplyTypeCode(invoice: {
  supTyp?: string;
  supplyType?: string;
  sup_typ?: string;
}): string {
  const inv = invoice as Record<string, unknown>;
  return String(inv.supTyp ?? inv.supplyType ?? inv.sup_typ ?? '').trim();
}

export function userHasEinvoiceCancelRole(roleNames: string[]): boolean {
  const allowed = getInvoiceConfig().einvoiceCancelAllowedRoleNames ?? ['Superadmin', 'DUMMY'];
  const allowedNorm = allowed.map((name) => name.trim().toLowerCase()).filter(Boolean);
  return roleNames.some((name) => allowedNorm.includes(name.trim().toLowerCase()));
}

